'use client';

import { useState, useCallback, useEffect, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import type { Database } from '@medizzconnect/db';
import { HeroSection } from './hero/hero-section';
import { CategoryScroll } from './categories/category-scroll';
import { ActiveFilters } from './filters/active-filters';
import { VideoGrid } from './videos/video-grid';
import { VideoModal } from '@/components/doctors/video-modal';
import type { VideosPage } from './videos/use-videos';

type MedicalCategoryRow = Database['public']['Tables']['medical_categories']['Row'];

interface HomePageClientProps {
  categories: MedicalCategoryRow[];
  initialVideos: VideosPage;
  /** Gesetzt wenn die Seite als /doctors/[uuid] gerendert wird – Modal sofort öffnen */
  initialProfileId?: string;
}

/**
 * Isoliert useSearchParams in einer winzigen invisible Komponente.
 * Nur diese landet in der Suspense-Grenze → der Rest wird statisch vorgerendert
 * (kein Spinner-Fallback → kein Footer-CLS von 0.29).
 */
function ProfileAutoOpener({ onOpen }: { onOpen: (id: string) => void }) {
  const searchParams = useSearchParams();
  useEffect(() => {
    const profileParam = searchParams.get('profile');
    if (profileParam) onOpen(profileParam);
  }, [searchParams, onOpen]);
  return null;
}

export function HomePageClient({ categories, initialVideos, initialProfileId }: HomePageClientProps) {
  const router = useRouter();
  const locale = useLocale();
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  // Auf /doctors/[uuid]-Seiten: Modal nach der Hydration öffnen (verhindert SSR-Mismatch)
  useEffect(() => {
    if (initialProfileId) {
      setSelectedVideoId(initialProfileId);
    }
  }, [initialProfileId]);

  const handleSelectVideo = useCallback((profileId: string) => {
    setSelectedVideoId(profileId);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedVideoId(null);
    // Auf /doctors/[uuid]-Seiten: zurück zur Homepage navigieren wenn Modal geschlossen
    if (initialProfileId) {
      const localePath = locale === 'de' ? '' : `/${locale}`;
      router.push(`${localePath}/`);
    }
  }, [initialProfileId, locale, router]);

  return (
    <>
      {/* useSearchParams nur hier – Suspense-Grenze um invisible Komponente,
          damit der Rest statisch vorgerendert wird (kein Layout-Shift) */}
      <Suspense fallback={null}>
        <ProfileAutoOpener onOpen={setSelectedVideoId} />
      </Suspense>

      <HeroSection />
      <CategoryScroll categories={categories} />
      <ActiveFilters categories={categories} />
      <VideoGrid initialData={initialVideos} onSelectVideo={handleSelectVideo} />

      {/* Video-Modal – öffnet sich über der Homepage wie in V1 */}
      {selectedVideoId && (
        <VideoModal
          profileId={selectedVideoId}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
}
